import { useCallback, useEffect, useState } from "react";

import { AvailableModel } from "../../../services/claude";
import {
  addLensToHistory,
  getCurrentLens,
  getLensNavigationInfo,
  LensHistoryItem,
  LensNavigationInfo,
  navigateToLens
} from "../utils/lensHistoryStorage";
import { LensNavigationDirection } from "../utils/lensNavigation";

interface AddToHistoryParams {
  content: string;
  model: AvailableModel;
  temperature: number;
  tokens: number;
  result: unknown;
  lensName?: string;
  interactionId?: string;
  tenantId?: string;
  tenantName?: string;
}

interface UseLensHistoryReturn {
  currentHistoryItem: LensHistoryItem | null;
  navigationInfo: LensNavigationInfo;
  navigateHistory: (direction: LensNavigationDirection) => void;
  addToHistory: (params: AddToHistoryParams) => Promise<{ added: boolean; historyItem: LensHistoryItem | null }>;
}

const useLensHistory = (): UseLensHistoryReturn => {
  const [currentHistoryItem, setCurrentHistoryItem] = useState<LensHistoryItem | null>(null);
  const [navigationInfo, setNavigationInfo] = useState<LensNavigationInfo>({
    canGoBack: false,
    canGoForward: false,
    currentIndex: -1,
    totalCount: 0
  });

  // Initialize state from localStorage on mount
  useEffect(() => {
    const currentItem = getCurrentLens();
    const navInfo = getLensNavigationInfo();
    setCurrentHistoryItem(currentItem);
    setNavigationInfo(navInfo);
  }, []);

  const navigateHistory = useCallback((direction: LensNavigationDirection): void => {
    const newHistoryItem = navigateToLens(direction);
    setCurrentHistoryItem(newHistoryItem);
    setNavigationInfo(getLensNavigationInfo());
  }, []);

  const addToHistory = useCallback(async (
    params: AddToHistoryParams
  ): Promise<{ added: boolean; historyItem: LensHistoryItem | null }> => {
    const { content, model, temperature, tokens, result, lensName, interactionId, tenantId, tenantName } = params;

    const historyResult = addLensToHistory({
      content,
      model,
      temperature,
      tokens,
      lastResult: result,
      lensName,
      interactionId,
      tenantId,
      tenantName
    });

    if (!historyResult.added) {
      return { added: false, historyItem: null };
    }

    const newHistoryItem = getCurrentLens();
    setCurrentHistoryItem(newHistoryItem);
    setNavigationInfo(getLensNavigationInfo());

    return { added: true, historyItem: newHistoryItem };
  }, []);

  return {
    currentHistoryItem,
    navigationInfo,
    navigateHistory,
    addToHistory
  };
};

export { useLensHistory };
